/**
 * COMPONENT: BedMissingBanner
 * En-tête du BED (cahier L16) : « une zone de texte en entête avec possibilité
 * d'afficher les informations manquantes […] quand il retourne sur le BED il y
 * a une mise en avant des éléments manquants jusqu'à finalisation complète ».
 *
 * Affiché en HAUT du bulletin à CHAQUE retour, tant qu'il reste un élément
 * manquant. Deux natures distinguées :
 *   - engagements « je fournirai avant le … » (le participant l'a choisi) ;
 *   - champs requis encore vides (il reste à les saisir).
 */

import { AlertTriangle, CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { formatDueDateFr, type BedMissingItem } from '../../lib/bed/wizard-model';

interface BedMissingBannerProps {
  items: BedMissingItem[];
  /** Titre alternatif après soumission (« votre dossier est incomplet »). */
  submitted?: boolean;
}

/**
 * Au-delà de ce seuil, la liste est repliée : à la première ouverture d'un
 * bulletin vierge elle ferait plusieurs dizaines de lignes et noierait
 * l'information au lieu de la mettre en avant.
 */
const COLLAPSE_THRESHOLD = 8;

function ItemList({ items }: { items: BedMissingItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, COLLAPSE_THRESHOLD);
  const hidden = items.length - visible.length;

  return (
    <>
      <ul className="space-y-1 text-sm">
        {visible.map((item) => (
          <li key={item.code} className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="font-medium">{item.label}</span>
            <span className="text-amber-700">— {item.role_label}</span>
            {item.kind === 'deferred' && formatDueDateFr(item.due_date) && (
              <span className="text-amber-700">· avant le {formatDueDateFr(item.due_date)}</span>
            )}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm font-medium text-amber-800 underline underline-offset-2"
        >
          Voir les {hidden} autres
        </button>
      )}
    </>
  );
}

export function BedMissingBanner({ items, submitted = false }: BedMissingBannerProps) {
  if (items.length === 0) return null;

  const deferred = items.filter((item) => item.kind === 'deferred');
  const empty = items.filter((item) => item.kind === 'empty');

  return (
    <div
      role="status"
      className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"
      data-testid="bed-missing-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-3">
          <p className="text-sm font-semibold">
            {submitted
              ? `Votre bulletin est enregistré mais incomplet — ${items.length} élément${
                  items.length > 1 ? 's' : ''
                } à fournir`
              : `${items.length} élément${items.length > 1 ? 's' : ''} manquant${
                  items.length > 1 ? 's' : ''
                } sur votre bulletin`}
          </p>

          {deferred.length > 0 && (
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-amber-700">
                <CalendarClock className="h-3.5 w-3.5" />
                Vous vous êtes engagé(e) à communiquer
              </p>
              <ItemList items={deferred} />
            </div>
          )}

          {empty.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
                Restent à renseigner
              </p>
              <ItemList items={empty} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
